/**
 * Authentication Service
 * Uses Amazon Cognito directly (without Amplify)
 * 
 * This provides the same functionality as Amplify Auth but with:
 * - Smaller bundle size
 * - More control over the auth flow
 * - Easier debugging
 */

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';
import apiClient from './api';

// ASS-60: wire the token resolver so ApiClient.request() can resolve
// a fresh token per request via the SDK's auto-refresh. This avoids a
// circular import (auth.js imports apiClient; api.js must not import auth.js)
// by injecting the resolver at module load time.
// The resolver is `getSession` — defined below — so we defer the wiring
// to after `getSession` is declared (see bottom of file).

// Cognito configuration - set via environment variables
const poolData = {
  UserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || '',
  ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
};

let userPool = null;

// Initialize user pool lazily
const getUserPool = () => {
  if (!userPool && poolData.UserPoolId && poolData.ClientId) {
    userPool = new CognitoUserPool(poolData);
  }
  return userPool;
};

/**
 * Get the current authenticated user
 */
export const getCurrentUser = () => {
  const pool = getUserPool();
  if (!pool) return null;
  return pool.getCurrentUser();
};

/**
 * Get current session (tokens)
 */
export const getSession = () => {
  return new Promise((resolve, reject) => {
    const user = getCurrentUser();
    
    if (!user) {
      resolve(null);
      return;
    }

    user.getSession((err, session) => {
      if (err) {
        reject(err);
        return;
      }

      if (session && session.isValid()) {
        // Set auth token for API calls
        const token = session.getIdToken().getJwtToken();
        apiClient.setAuthToken(token);
        
        resolve({
          user,
          session,
          tokens: {
            idToken: session.getIdToken().getJwtToken(),
            accessToken: session.getAccessToken().getJwtToken(),
            refreshToken: session.getRefreshToken().getToken(),
          },
        });
      } else {
        resolve(null);
      }
    });
  });
};

/**
 * Sign in with username/email and password
 */
export const signIn = (username, password) => {
  return new Promise((resolve, reject) => {
    const pool = getUserPool();
    if (!pool) {
      reject(new Error('Cognito not configured'));
      return;
    }

    const user = new CognitoUser({
      Username: username,
      Pool: pool,
    });

    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    });

    user.authenticateUser(authDetails, {
      onSuccess: (session) => {
        // Set auth token for API calls
        const token = session.getIdToken().getJwtToken();
        apiClient.setAuthToken(token);

        resolve({
          user,
          session,
          challengeName: null,
        });
      },
      onFailure: (err) => {
        reject(err);
      },
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        // User needs to set a new password
        resolve({
          user,
          session: null,
          challengeName: 'NEW_PASSWORD_REQUIRED',
          userAttributes,
          requiredAttributes,
        });
      },
      mfaRequired: (challengeName, challengeParameters) => {
        resolve({
          user,
          session: null,
          challengeName: 'MFA_REQUIRED',
          challengeParameters,
        });
      },
    });
  });
};

/**
 * Complete new password challenge
 */
export const completeNewPassword = (user, newPassword, userAttributes = {}) => {
  return new Promise((resolve, reject) => {
    // Remove non-writable attributes
    delete userAttributes.email_verified;
    delete userAttributes.phone_number_verified;

    user.completeNewPasswordChallenge(newPassword, userAttributes, {
      onSuccess: (session) => {
        const token = session.getIdToken().getJwtToken();
        apiClient.setAuthToken(token);
        resolve({ user, session });
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
};

/**
 * Sign out current user
 */
export const signOut = () => {
  return new Promise((resolve) => {
    const user = getCurrentUser();
    
    if (user) {
      user.signOut();
    }
    
    apiClient.clearAuthToken();
    resolve();
  });
};

/**
 * Global sign out (invalidate all sessions)
 */
export const globalSignOut = () => {
  return new Promise((resolve, reject) => {
    const user = getCurrentUser();
    
    if (!user) {
      resolve();
      return;
    }

    user.globalSignOut({
      onSuccess: () => {
        apiClient.clearAuthToken();
        resolve();
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
};

/**
 * Forgot password - send reset code
 */
export const forgotPassword = (username) => {
  return new Promise((resolve, reject) => {
    const pool = getUserPool();
    if (!pool) {
      reject(new Error('Cognito not configured'));
      return;
    }

    const user = new CognitoUser({
      Username: username,
      Pool: pool,
    });

    user.forgotPassword({
      onSuccess: (data) => {
        resolve(data);
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
};

/**
 * Forgot password - submit new password with code
 */
export const forgotPasswordSubmit = (username, code, newPassword) => {
  return new Promise((resolve, reject) => {
    const pool = getUserPool();
    if (!pool) {
      reject(new Error('Cognito not configured'));
      return;
    }

    const user = new CognitoUser({
      Username: username,
      Pool: pool,
    });

    user.confirmPassword(code, newPassword, {
      onSuccess: () => {
        resolve();
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
};

/**
 * Change password for authenticated user
 */
export const changePassword = (oldPassword, newPassword) => {
  return new Promise((resolve, reject) => {
    const user = getCurrentUser();
    
    if (!user) {
      reject(new Error('No authenticated user'));
      return;
    }

    user.getSession((err, session) => {
      if (err) {
        reject(err);
        return;
      }

      user.changePassword(oldPassword, newPassword, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  });
};

/**
 * Get user attributes
 */
export const getUserAttributes = () => {
  return new Promise((resolve, reject) => {
    const user = getCurrentUser();
    
    if (!user) {
      reject(new Error('No authenticated user'));
      return;
    }

    user.getSession((err) => {
      if (err) {
        reject(err);
        return;
      }

      user.getUserAttributes((err, attributes) => {
        if (err) {
          reject(err);
          return;
        }

        // Convert to object
        const attrs = {};
        attributes.forEach((attr) => {
          attrs[attr.getName()] = attr.getValue();
        });
        resolve(attrs);
      });
    });
  });
};

/**
 * Get user groups from token
 */
export const getUserGroups = async () => {
  const session = await getSession();
  if (!session) return [];

  const idToken = session.session.getIdToken();
  const payload = idToken.decodePayload();
  
  return payload['cognito:groups'] || [];
};

/**
 * Check if user is member of a group
 */
export const isMemberOf = async (groupPattern) => {
  const groups = await getUserGroups();
  
  if (typeof groupPattern === 'string') {
    return groups.includes(groupPattern);
  }
  
  if (groupPattern instanceof RegExp) {
    return groups.some((group) => groupPattern.test(group));
  }
  
  return false;
};

/**
 * Refresh session tokens
 */
export const refreshSession = () => {
  return new Promise((resolve, reject) => {
    const user = getCurrentUser();
    
    if (!user) {
      resolve(null);
      return;
    }

    user.getSession((err, session) => {
      if (err) {
        reject(err);
        return;
      }

      const refreshToken = session.getRefreshToken();

      user.refreshSession(refreshToken, (err, newSession) => {
        if (err) {
          reject(err);
          return;
        }

        const token = newSession.getIdToken().getJwtToken();
        apiClient.setAuthToken(token);
        
        resolve({
          user,
          session: newSession,
        });
      });
    });
  });
};

// ASS-60: wire the token resolver now that getSession is declared.
// This runs once at module load time. Every subsequent ApiClient.request()
// call will resolve a fresh token via getSession() before attaching the
// Authorization header.
apiClient.setTokenResolver(getSession);

export default {
  getCurrentUser,
  getSession,
  signIn,
  signOut,
  globalSignOut,
  completeNewPassword,
  forgotPassword,
  forgotPasswordSubmit,
  changePassword,
  getUserAttributes,
  getUserGroups,
  isMemberOf,
  refreshSession,
};
